
import React, { FC, ReactElement, useEffect, useState } from "react";
import Modal from 'react-modal';
import { ENROLL_TYPE, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../constants/command-status";
import { EnrolSubCmds } from "../../constants/sub-commands";
import { WS_URL } from "../../constants/ws-constants";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../../contexts/websocket/IcaWebsocketContext";
import { EnrolmentTask } from "../../models/EnrolmentTask";
import { ExecuteCommand } from "../../models/ExecuteCommand";
import { Person } from "../../models/Person";
import { Tag } from "../../models/Tag";
import WizardSideBarStep from "../../models/WizardSideBarStep";
import { requestEnrolDevice } from "../../services/executeCommandService";
import { IShowEnrollValidOptions } from "../../services/wsPluginService/plugins/base/pluginModels";
import { checkPluginValidatedList, isPluginValidationListInterface } from "../../services/wsPluginService/plugins/decodePluginMsg";
import { validatePluginMsg } from "../../services/wsPluginService/plugins/validatePluginMsg";
import WSPluginService from "../../services/wsPluginService/wsPluginService";
import { useDialog } from "../dialog/DialogContext";
import Wizard from "../wizard/wizard";
import EditCardWizard from "./card/edit-card-wizard";
import EnrolCardWizard from "./card/enrol-card-wizard";
import EnrolFaceWizard from "./face/enrol-face-wizard";
import EnrolFingerWizard from "./finger/enrol-finger-wizard";
import EnrolHandWizard from "./hand/enrol-hand-wizard";
import EnrolMobileWizard from "./mobile/enrol-mobile-wizard";
import TasksForm from "./tasks-form";
import EnrolQrCodeWizard from "./qr-code/enrol-qrcode-wizard";
import EncodeCardWizard from "./encode/encode-card-wizard";

type Props = {
    onClose: () => void
    isOpen: boolean
    onCardSave: (card: Tag) => void,
    currentCredential: Tag,
    currentItem: Person;
    onRequestMobileCredential: (values: any) => void;
    updateBadge: (badge: number) => void;
};

const EnrolWizard: FC<Props> = ({ onClose, isOpen, onCardSave, currentCredential, currentItem, onRequestMobileCredential, updateBadge }) => {
    const { translate } = useLocale();
    const [taskId, setTaskId] = useState<number>(0);
    const defaultPluginsAvailable: IShowEnrollValidOptions = { mde: false, camera: false, print: false, omnikey: false, morpho: false, suprema: false, nitgen: false, extension: false, lumidigm: false, controlid: false };
    const [pluginsAvailable, setPluginsAvailable] = useState<IShowEnrollValidOptions>(defaultPluginsAvailable);
    const [pluginEnrolMsg, setPluginEnrolMsg] = useState<string>(''); // for plugin websocket messages
    const [pluginWSInstance, setPluginWSInstance] = useState<WebSocket | undefined>();
    const [taskDescription, setTaskDescription] = useState<string>('Pending');
    const [availableSystemDeviceIds, setAvailableSystemDeviceIds] = useState<number[]>([])
    const enrolmentTask = new EnrolmentTask(translate);
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();
    const [hasTags, setHasTags] = useState<boolean>(false);

    useEffect(() => {
        if (!isOpen) {
            resetKeyField();
        }

        setHasTags(currentItem.totalTags > 0);
    }, [isOpen])

    useEffect(() => {
        // is there actually a valid message here
        if (typeof pluginEnrolMsg === "string" && pluginEnrolMsg.length > 5) {

            // make sure we have an instance and the ws is open
            if (pluginWSInstance !== undefined && pluginWSInstance.readyState === WebSocket.OPEN) {
                // send it!
                pluginWSInstance.send(pluginEnrolMsg);
            }
        }

        // it has text, therefore set to '' again
        if (pluginEnrolMsg !== undefined) {
            setPluginEnrolMsg('');
        }

    }, [pluginEnrolMsg]);

    /**
   *
   * @param event
   * This function basically reset everything to original state.
   */
    const resetKeyField = () => {
        setTaskId(0);
        setTaskDescription("Pending");
    }

    const closeWizard = () => {
        pluginWSInstance?.close(1000, "Closing view");
        icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        setAvailableSystemDeviceIds([]);
        setPluginsAvailable(defaultPluginsAvailable);
        onClose();
    }

    const onWebSocketError = (error) => {
        console.log('Plugin Manager not detected. Some enrolment capabilities will be unavailable.');
        setPluginWSInstance(undefined);
    };

    const onWebSocketClosed = (e) => {
        setPluginWSInstance(undefined);
    };

    const onWebSocketOpen = (instance, value) => {
        // set the instance so we can use anywhere here
        setPluginWSInstance(instance);
        // set the validation message to send first so we can get a list of validate plugins
        setPluginEnrolMsg(validatePluginMsg());
    };

    const onMessageReceived = (value) => {
        if (value !== undefined && isPluginValidationListInterface(value)) {
            // validation message
            const validPlugins = checkPluginValidatedList(value) as IShowEnrollValidOptions;
            setPluginsAvailable(validPlugins);
        }
    };

    const getWizard = () => {
        if (currentCredential.id != 0) {
            return <EditCardWizard
                cardInfo={currentCredential}
                onClose={onClose}
                onSave={onCardSave} />
        }

        if (taskId === enrolmentTask.ENROL_QR_CODE.id) {
            return <EnrolQrCodeWizard
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={content}
                defaultSteps={steps}
                onBack={resetKeyField}
                updateBadge={updateBadge}
                hasTags={hasTags}
                masterId={currentItem.id} />
        }
        else if (taskId === enrolmentTask.ENROL_CARD.id) {
            return <EnrolCardWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={content}
                defaultSteps={steps}
                onBack={resetKeyField}
                websocket={pluginWSInstance}
                onSave={onCardSave} />
        }
        else if (taskId === enrolmentTask.ENROL_MOBILE_CREDENTIAL.id) {
            return <EnrolMobileWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                cardInfo={currentCredential}
                defaultContent={content}
                defaultSteps={steps}
                onFieldReset={resetKeyField}
                onSave={onRequestMobileCredential}
                onClose={closeWizard}
                userEmail={currentItem.email} />
        }
        else if (taskId === enrolmentTask.ENROL_FINGERPRINT.id) {
            return <EnrolFingerWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={content}
                defaultSteps={steps}
                onBack={resetKeyField}
                websocket={pluginWSInstance}
                updateBadge={updateBadge}
                masterId={currentItem.id}
                hasTags={hasTags} />
        }
        else if (taskId === enrolmentTask.ENROL_HAND.id) {
            return <EnrolHandWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={content}
                defaultSteps={steps}
                onBack={resetKeyField}
                updateBadge={updateBadge}
                masterId={currentItem.id} />
        }
        else if (taskId === enrolmentTask.ENROL_FACE.id) {
            return <EnrolFaceWizard
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds}
                cardInfo={currentCredential}
                onClose={closeWizard}
                defaultContent={content}
                defaultSteps={steps}
                onBack={resetKeyField}
                updateBadge={updateBadge}
                pluginWSInstance={pluginWSInstance}
                hasTags={hasTags}
                masterId={currentItem.id} />
        }
        else if (taskId === enrolmentTask.ENCODE_CARD.id) {
            return <EncodeCardWizard
                cardInfo={currentCredential}
                defaultContent={content}
                defaultSteps={steps}
                onBack={resetKeyField}
                onClose={closeWizard}
                onSave={onCardSave}
                availablePlugins={pluginsAvailable}
                availableSystemDeviceIds={availableSystemDeviceIds} />
        }
        else {
            return <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                step={0}
                onClose={closeWizard}
                sidebarSteps={steps}>
                {content}
            </Wizard>
        }
    }

    const onDetailsNext = (event: any) => {
        if (event.task) {
            setTaskId(event.id);
            setTaskDescription(event.description)
        }
    };

    const content: ReactElement[] = [
        <TasksForm
            pluginDevice={pluginsAvailable}
            availableSystemDeviceIds={availableSystemDeviceIds}
            onNext={onDetailsNext} />,
    ];

    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.tasks),
            description: taskDescription,
            substeps: []
        }
    ];

    useEffect(() => {
        const command: ExecuteCommand = {
            cmd: ENROLL_TYPE.ENROLL,
            cmdSubType: EnrolSubCmds.Enroll_Devices_Available
        }

        requestEnrolDevice(command).then((response) => {
            setAvailableSystemDeviceIds(response.ids);
        }).catch(() => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.availableSystemDeviceFailure) });
            setAvailableSystemDeviceIds([]);
        });
    }, [isOpen === true])

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessageReceived)
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none w-content">
        <WSPluginService
            onMessage={onMessageReceived}
            onOpen={onWebSocketOpen}
            onError={onWebSocketError}
            onClosed={onWebSocketClosed}
            url={window.location.protocol === "https:" ? WS_URL.SECURED_PLUGIN_MANAGER_WEBSOCKET : WS_URL.UNSECURED_PLUGIN_MANAGER_WEBSOCKET} />
        {getWizard()}
    </Modal>
}


export default EnrolWizard;
